/**
 * TermsScreen — Conditions d'utilisation
 *
 * Présente les CGU de l'app MyTenue. Conformes aux exigences App Store Apple
 * §5.1.1 (i-iv) et Google Play. Le texte couvre :
 *   - Acceptation des CGU
 *   - Description du service
 *   - Compte utilisateur et obligations
 *   - Abonnements premium et facturation (auto-renouvellement)
 *   - Propriété intellectuelle
 *   - Limitations de responsabilité
 *   - Résiliation
 *   - Loi applicable et juridiction (France)
 *
 * IMPORTANT : avant publication, faire valider le texte par un avocat ou
 * un service comme Legalstart / Captain Contrat pour la France.
 */
import React, { useEffect } from 'react';
import {
  View, Text, ScrollView, StyleSheet, SafeAreaView,
  TouchableOpacity, StatusBar, Platform, useWindowDimensions,
} from 'react-native';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../i18n';
import BackButton from '../components/BackButton';
import { openLegalUrl } from '../utils/openLegalUrl';

export default function TermsScreen({ navigation }) {
  const { dark, theme } = useTheme();
  const { lang } = useI18n();
  const en = lang === 'en';
  const s = makeStyles(theme);
  const { height: winHeight } = useWindowDimensions();

  useEffect(() => {
    openLegalUrl('terms', lang);
    const t = setTimeout(() => { try { navigation?.goBack?.(); } catch {} }, 400);
    return () => clearTimeout(t);
  }, [lang, navigation]);

  return (
    <SafeAreaView style={s.safe}>
      <StatusBar barStyle={dark ? 'light-content' : 'dark-content'} />

      {/* Header */}
      <View style={s.header}>
        <BackButton onPress={() => {
          try {
            if (navigation.canGoBack && navigation.canGoBack()) navigation.goBack();
            else navigation.navigate('Main');
          } catch { try { navigation.navigate('Main'); } catch {} }
        }} />
        <Text style={s.title}>{en ? 'Terms of Service' : "Conditions d'utilisation"}</Text>
        <View style={{ width: 38 }} />
      </View>

      <ScrollView
        contentContainerStyle={[s.content, { paddingBottom: 80 }]}
        style={Platform.OS === 'web' ? { height: winHeight - 60, overflowY: 'auto', WebkitOverflowScrolling: 'touch' } : undefined}
      >
        <Text style={s.lastUpdate}>{en ? 'Last updated: May 21, 2026' : 'Dernière mise à jour : 21 mai 2026'}</Text>

        <Text style={s.intro}>
          {en
            ? `Welcome to MyTenue. These terms of service ("Terms") govern your access to and use of the MyTenue mobile and web application ("the Application", "the Service"). By downloading, installing, or using MyTenue, you agree to be bound by these Terms. If you do not accept these terms, do not use the Application.`
            : `Bienvenue sur MyTenue. Les présentes conditions générales d'utilisation (« CGU ») régissent votre accès et votre utilisation de l'application mobile et web MyTenue (« l'Application », « le Service »). En téléchargeant, installant ou utilisant MyTenue, vous acceptez d'être lié par ces CGU. Si vous n'acceptez pas ces conditions, n'utilisez pas l'Application.`}
        </Text>

        {/* v1.12.153 — Identité de l'éditeur */}
        <Section title={en ? '0. Service publisher' : '0. Éditeur du service'}>
          <Para>
            {en
              ? `The MyTenue application and website (www.mytenue.com) are published by:`
              : `L'application et le site web MyTenue (www.mytenue.com) sont édités par :`}
          </Para>
          <Para>
            <Text style={s.bold}>TO BE SMART</Text>{en ? ' — SAS with share capital of EUR 1' : ' — SAS au capital de 1 €'}
          </Para>
          <Para>
            <Text style={s.bold}>{en ? 'Registered office:' : 'Siège social :'}</Text> 17 rue Benoit Malon, 92150 Suresnes, France
          </Para>
          <Para>
            <Text style={s.bold}>SIRET:</Text> 928 276 971 00027
          </Para>
          <Para>
            <Text style={s.bold}>{en ? 'Contact:' : 'Contact :'}</Text> contact@mytenue.com — +33 7 87 45 47 32
          </Para>
          <Para>
            <Text style={s.bold}>{en ? 'Target audience:' : 'Audience cible :'}</Text> {en
              ? `France, French overseas territories, and French-speaking countries (Belgium, Switzerland, Luxembourg).`
              : `France, DOM-TOM, et pays francophones (Belgique, Suisse, Luxembourg).`}
          </Para>
        </Section>

        <Section title={en ? '1. Description of the service' : '1. Description du service'}>
          <Para>
            {en
              ? `MyTenue is a personal styling application based on artificial intelligence. It allows users to:`
              : `MyTenue est une application de stylisme personnel basée sur l'intelligence artificielle. Elle permet aux utilisateurs de :`}
          </Para>
          <Bullet>{en ? 'Photograph and organise their wardrobe' : 'Photographier et organiser leur garde-robe (« dressing »)'}</Bullet>
          <Bullet>{en ? 'Receive AI-generated outfit suggestions based on their garments, the weather, and their preferences' : 'Recevoir des suggestions de tenues générées par IA en fonction de leurs vêtements, de la météo et de leurs préférences'}</Bullet>
          <Bullet>{en ? 'Discover fashion products from partners (Zalando, ASOS, H&M, Zara, Cashmere, and iQueens)' : 'Découvrir des produits de mode chez des partenaires (Zalando, ASOS, H&M, Zara, Cashmere et iQueens)'}</Bullet>
          <Bullet>{en ? 'Build suitcases tailored to their travels' : 'Composer des valises adaptées à leurs voyages'}</Bullet>
          <Bullet>{en ? 'Earn cashback on purchases made through partner links' : "Bénéficier d'un cashback sur les achats effectués via les liens partenaires"}</Bullet>
        </Section>

        <Section title={en ? '2. Account creation' : '2. Création de compte'}>
          <Para>
            {en
              ? `Use of the Application requires the creation of an account. You can register via Apple Sign-In, Google Sign-In, or email. You undertake to:`
              : `L'utilisation de l'Application nécessite la création d'un compte. Vous pouvez vous inscrire via Apple Sign-In, Google Sign-In ou par email. Vous vous engagez à :`}
          </Para>
          <Bullet>{en ? 'Provide accurate, complete, and up-to-date information' : 'Fournir des informations exactes, complètes et à jour'}</Bullet>
          <Bullet>{en ? 'Maintain the confidentiality of your login credentials' : 'Maintenir la confidentialité de vos identifiants de connexion'}</Bullet>
          <Bullet>{en ? 'Immediately notify MyTenue of any unauthorised use of your account' : 'Notifier immédiatement MyTenue de toute utilisation non autorisée de votre compte'}</Bullet>
          <Bullet>{en ? 'Be at least 13 years old (16 years old in the European Union)' : "Être âgé(e) d'au moins 13 ans (16 ans dans l'Union Européenne)"}</Bullet>
        </Section>

        <Section title={en ? '3. Premium subscriptions' : '3. Abonnements premium'}>
          <Para>
            {en
              ? `MyTenue offers a free version with usage limits and several paid subscription plans (Basic, Premium, Gold) on a monthly or annual basis, as well as one-off add-ons (extra try-ons, AI scans, wardrobe slots).`
              : `MyTenue propose une version gratuite avec des limites d'utilisation et plusieurs formules d'abonnement payantes (Basic, Premium, Gold) en mensuel ou annuel, ainsi que des options ponctuelles (essais virtuels, analyses IA et emplacements dressing supplémentaires).`}
          </Para>
          <Para>
            <Text style={s.bold}>{en ? 'Payment methods:' : 'Moyens de paiement :'}</Text> {en
              ? `on the web version (www.mytenue.com), payments are processed by Stripe (card). On the mobile applications, subscriptions are managed via the App Store (iOS) or Google Play (Android).`
              : `sur la version web (www.mytenue.com), les paiements sont traités par Stripe (carte bancaire). Sur les applications mobiles, les abonnements sont gérés via l'App Store (iOS) ou Google Play (Android).`}
          </Para>
          <Para>
            <Text style={s.bold}>{en ? 'Billing and auto-renewal:' : 'Facturation et auto-renouvellement :'}</Text> {en
              ? `payment is charged at the time of purchase confirmation and is automatically renewed at the end of each period (monthly or annual), unless cancelled before the end of the current period.`
              : `le paiement est débité au moment de la confirmation d'achat puis renouvelé automatiquement à la fin de chaque période (mensuelle ou annuelle), sauf résiliation avant la fin de la période en cours.`}
          </Para>
          <Para>
            <Text style={s.bold}>{en ? 'Management and cancellation:' : 'Gestion et résiliation :'}</Text> {en
              ? `web subscriptions can be managed and cancelled directly from the Profile screen. Mobile subscriptions are managed in your App Store or Google Play account settings. Cancellation takes effect at the end of the current billing period — you keep access to your plan until that date.`
              : `les abonnements web se gèrent et se résilient directement depuis l'écran Profil. Les abonnements mobiles se gèrent dans les réglages de votre compte App Store ou Google Play. La résiliation prend effet à la fin de la période de facturation en cours — vous conservez l'accès à votre formule jusqu'à cette date.`}
          </Para>
          <Para>
            <Text style={s.bold}>{en ? 'Plan changes:' : 'Changement de formule :'}</Text> {en
              ? `you may switch plans at any time. An upgrade takes effect immediately with a prorated charge calculated by Stripe; a downgrade or a switch to annual billing takes effect at the end of the current period.`
              : `vous pouvez changer de formule à tout moment. Une montée en gamme prend effet immédiatement avec un prorata calculé par Stripe ; une descente en gamme ou un passage à l'annuel prend effet à la fin de la période en cours.`}
          </Para>
          <Para>
            <Text style={s.bold}>{en ? 'Refunds:' : 'Remboursement :'}</Text> {en
              ? `the detailed terms (right of withdrawal, refunds) are set out in the Terms of Sale. For mobile purchases, refund requests are handled directly by Apple (App Store) or Google (Play Store).`
              : `les modalités détaillées (droit de rétractation, remboursements) figurent dans les Conditions Générales de Vente. Pour les achats mobiles, les demandes de remboursement sont traitées directement par Apple (App Store) ou Google (Play Store).`}
          </Para>
        </Section>

        <Section title={en ? '4. Intellectual property' : '4. Propriété intellectuelle'}>
          <Para>
            {en
              ? `The Application, its code, its design, its features, and all of its content (text, images, logos, trademarks) are the exclusive property of MyTenue or its licensors. Any unauthorised reproduction, representation, modification, or exploitation is strictly prohibited.`
              : `L'Application, son code, son design, ses fonctionnalités et tous ses contenus (textes, images, logos, marques) sont la propriété exclusive d'MyTenue ou de ses concédants de licence. Toute reproduction, représentation, modification ou exploitation non autorisée est strictement interdite.`}
          </Para>
          <Para>
            {en
              ? `The photographs you upload to your wardrobe remain your property. By uploading them, you grant MyTenue a limited licence to store them, analyse them via AI, and provide you with the service.`
              : `Les photographies que vous téléchargez dans votre dressing restent votre propriété. En les téléchargeant, vous accordez à MyTenue une licence limitée pour les stocker, les analyser via IA et vous fournir le service.`}
          </Para>
        </Section>

        <Section title={en ? '5. Acceptable use' : '5. Utilisation acceptable'}>
          <Para>{en ? 'You undertake not to:' : 'Vous vous engagez à ne pas :'}</Para>
          <Bullet>{en ? 'Use the Application for illegal or unauthorised purposes' : "Utiliser l'Application à des fins illégales ou non autorisées"}</Bullet>
          <Bullet>{en ? "Attempt to access, modify, or disrupt the Application's servers" : "Tenter d'accéder, modifier ou perturber les serveurs de l'Application"}</Bullet>
          <Bullet>{en ? 'Reproduce, copy, sell, or commercially exploit all or part of the Service' : 'Reproduire, copier, vendre ou exploiter commercialement tout ou partie du Service'}</Bullet>
          <Bullet>{en ? "Upload images containing illegal or offensive content, or content that infringes others' rights" : "Télécharger des images contenant des contenus illégaux, offensants, ou portant atteinte aux droits d'autrui"}</Bullet>
          <Bullet>{en ? 'Create multiple accounts to circumvent usage limits' : "Créer plusieurs comptes pour contourner les limites d'utilisation"}</Bullet>
        </Section>

        <Section title={en ? '6. Partner links and purchases' : '6. Liens partenaires et achats'}>
          <Para>
            {en
              ? `The Application contains links to commercial partner websites. MyTenue receives a commission when you purchase through these links, which funds the development of the service and allows us to give a share back to you in the form of cashback.`
              : `L'Application contient des liens vers des sites de partenaires commerciaux. MyTenue perçoit une commission lorsque vous achetez via ces liens, qui finance le développement du service et permet de vous reverser une partie sous forme de cashback.`}
          </Para>
          <Para>
            {en
              ? `MyTenue is not the seller of the products offered and does not intervene in the transaction between you and the partner. Any complaint relating to a purchase (delivery, quality, return) must be addressed to the partner concerned.`
              : `MyTenue n'est pas vendeur des produits proposés et n'intervient pas dans la transaction entre vous et le partenaire. Toute réclamation relative à un achat (livraison, qualité, retour) doit être adressée au partenaire concerné.`}
          </Para>
        </Section>

        <Section title={en ? '7. Limitation of liability' : '7. Limitation de responsabilité'}>
          <Para>
            {en
              ? `The Application is provided "as is". MyTenue does not guarantee that the Service will be uninterrupted, error-free, or will meet all your expectations. Outfit recommendations are generated by artificial intelligence and do not constitute professional advice.`
              : `L'Application est fournie « en l'état ». MyTenue ne garantit pas que le Service sera ininterrompu, sans erreur ou répondra à toutes vos attentes. Les recommandations de tenues sont générées par intelligence artificielle et ne constituent pas des conseils professionnels.`}
          </Para>
          <Para>
            {en
              ? `To the extent permitted by law, MyTenue's liability cannot be engaged for indirect, intangible, or consequential damages resulting from the use of the Application.`
              : `Dans les limites autorisées par la loi, la responsabilité d'MyTenue ne saurait être engagée pour des dommages indirects, immatériels ou consécutifs résultant de l'utilisation de l'Application.`}
          </Para>
        </Section>

        <Section title={en ? '8. Termination' : '8. Résiliation'}>
          <Para>
            {en
              ? `You can delete your account at any time from the Profile screen of the Application. MyTenue reserves the right to suspend or terminate your account in case of violation of these Terms, in particular article 5.`
              : `Vous pouvez supprimer votre compte à tout moment depuis l'écran Profil de l'Application. MyTenue se réserve le droit de suspendre ou résilier votre compte en cas de violation des présentes CGU, notamment de l'article 5.`}
          </Para>
          <Para>
            {en
              ? `Termination results in the deletion of your account and all your data (wardrobe, photos, "My model" personal photo, preferences) in accordance with our Privacy Policy.`
              : `La résiliation entraîne la suppression de votre compte et de toutes vos données (dressing, photos, photo perso « Mon mannequin », préférences) conformément à notre Politique de confidentialité.`}
          </Para>
        </Section>

        <Section title={en ? '9. Security and data protection' : '9. Sécurité et protection des données'}>
          <Para>
            <Text style={{ fontWeight: '700' }}>{en ? 'All your personal data is protected and encrypted.' : 'Toutes vos données personnelles sont protégées et chiffrées.'}</Text>
            {'\n\n'}
            {en
              ? `MyTenue applies the highest security standards to guarantee the confidentiality of your data:`
              : `MyTenue applique les standards de sécurité les plus élevés pour garantir la confidentialité de vos données :`}
          </Para>
          <Para>
            {en ? '• TLS 1.3 encryption for all network communications' : '• Chiffrement TLS 1.3 pour toutes les communications réseau'}{'\n'}
            {en ? '• AES-256 encryption at rest for all photos (wardrobe, avatar, "My model" personal photo)' : '• Chiffrement au repos AES-256 pour toutes les photos (dressing, avatar, photo perso « Mon mannequin »)'}{'\n'}
            {en ? '• PostgreSQL database encrypted via AWS KMS' : '• Base de données PostgreSQL chiffrée via AWS KMS'}{'\n'}
            {en ? '• Private S3 buckets with access only via temporary signed URLs (1h expiration)' : '• Buckets S3 privés avec accès uniquement via URLs signées temporaires (expiration 1h)'}{'\n'}
            {en ? '• Hosting in Europe (AWS Paris region)' : '• Hébergement en Europe (AWS région Paris)'}{'\n'}
            {en ? '• Multi-factor authentication for admin access' : '• Authentification multi-facteurs pour les accès admin'}
          </Para>
          <Para>
            {en
              ? `The "My model" personal photo used to personalise virtual try-ons is stored encrypted and is never shared with third parties or used to train any AI model.`
              : `La photo perso « Mon mannequin » utilisée pour personnaliser les essayages virtuels est stockée chiffrée et n'est jamais partagée avec des tiers, ni utilisée pour entraîner un modèle d'IA.`}
          </Para>
          <Para>
            {en
              ? `Deletion of your account automatically results in the permanent erasure of all your data, including the "My model" personal photo.`
              : `La suppression de votre compte entraîne automatiquement l'effacement définitif de l'ensemble de vos données, y compris la photo perso « Mon mannequin ».`}
          </Para>
        </Section>

        <Section title={en ? '10. Changes to the Terms' : '10. Modifications des CGU'}>
          <Para>
            {en
              ? `MyTenue reserves the right to modify these Terms at any time. Changes take effect upon their publication in the Application. You will be notified of substantial changes. Continued use after notification constitutes acceptance of the new terms.`
              : `MyTenue se réserve le droit de modifier ces CGU à tout moment. Les modifications prennent effet dès leur publication dans l'Application. Vous serez notifié des changements substantiels. La poursuite de l'utilisation après notification vaut acceptation des nouvelles conditions.`}
          </Para>
        </Section>

        <Section title={en ? '11. Applicable law and jurisdiction' : '11. Loi applicable et juridiction'}>
          <Para>
            {en
              ? `These Terms are governed by French law. Any dispute relating to their interpretation or performance falls within the exclusive jurisdiction of the French courts, subject to the mandatory provisions applicable to consumers.`
              : `Les présentes CGU sont soumises au droit français. Tout litige relatif à leur interprétation ou exécution relève de la compétence exclusive des tribunaux français, sous réserve des dispositions impératives applicables aux consommateurs.`}
          </Para>
          <Para>
            {en
              ? `In accordance with article L. 612-1 of the French Consumer Code, you have the option of resorting free of charge to a consumer mediator with a view to the amicable resolution of a dispute.`
              : `Conformément à l'article L. 612-1 du Code de la consommation, vous avez la possibilité de recourir gratuitement à un médiateur de la consommation en vue de la résolution amiable d'un litige.`}
          </Para>
        </Section>

        <Section title={en ? '12. Contact' : '12. Contact'}>
          <Para>
            {en
              ? `For any question regarding these Terms, you can contact us at:`
              : `Pour toute question concernant ces CGU, vous pouvez nous contacter à :`}
          </Para>
          <Text style={s.contact}>contact@mytenue.com</Text>
        </Section>

        <View style={{ height: 40 }} />
      </ScrollView>
    </SafeAreaView>
  );
}

// Sous-composants
function Section({ title, children }) {
  const { theme } = useTheme();
  const s = makeStyles(theme);
  return (
    <View style={s.section}>
      <Text style={s.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}
function Para({ children, style }) {
  const { theme } = useTheme();
  const s = makeStyles(theme);
  return <Text style={[s.para, style]}>{children}</Text>;
}
function Bullet({ children }) {
  const { theme } = useTheme();
  const s = makeStyles(theme);
  return (
    <View style={s.bulletRow}>
      <Text style={s.bulletDot}>•</Text>
      <Text style={s.bulletText}>{children}</Text>
    </View>
  );
}

const makeStyles = (theme) => StyleSheet.create({
  safe: { flex: 1, backgroundColor: theme.bg },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingVertical: 12,
    paddingTop: Platform.OS === 'android'
      ? (StatusBar.currentHeight || 24) + 8
      : 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: theme.bgBorder,
  },
  backBtn: {
    width: 38, height: 38,
    borderRadius: 19,
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: theme.bgRaised || theme.bgSurface || '#F5F4F0',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: theme.bgBorder,
  },
  backIcon: {
    fontSize: 28,
    color: theme.text,
    fontWeight: '300',
    marginLeft: -2,
    marginTop: -2,
  },
  back: { fontSize: 14, color: theme.textSec, fontWeight: '500' }, // legacy, gardé au cas où
  title: { fontSize: 16, fontWeight: '600', color: theme.text, letterSpacing: 0.3 },
  content: { paddingHorizontal: 20, paddingTop: 20, paddingBottom: 60 },
  lastUpdate: {
    fontSize: 11,
    color: theme.textFaint,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    marginBottom: 20,
  },
  intro: {
    fontSize: 14,
    lineHeight: 22,
    color: theme.textSec,
    marginBottom: 24,
  },
  section: { marginBottom: 24 },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.text,
    marginBottom: 10,
    letterSpacing: 0.2,
  },
  para: {
    fontSize: 14,
    lineHeight: 22,
    color: theme.textSec,
    marginBottom: 10,
  },
  bold: { fontWeight: '600', color: theme.text },
  bulletRow: {
    flexDirection: 'row',
    marginBottom: 6,
    paddingLeft: 4,
  },
  bulletDot: {
    width: 16,
    fontSize: 14,
    color: theme.accent,
    lineHeight: 22,
  },
  bulletText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 22,
    color: theme.textSec,
  },
  contact: {
    fontSize: 14,
    color: theme.accent,
    fontWeight: '500',
    marginTop: 4,
  },
});
